import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Tabs,
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Breadcrumbs,
  BreadcrumbItem,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { useIsHidStaff } from "../../../../../../hooks/useIsHidStaff";
import ValidationTab from "./components/ValidationTab/index";
import WhitelabelTab from "./components/WhitelabelTab/index";
import { getIntegrationDetailSagaAction } from "../../../../../../redux/saga/projects/saga-actions";
import { validationDocumentationSliceAction } from "../../../../../../redux/slices/validation-documentation";
import "./ValidationPage.scss";
import { projectValidationSliceAction } from "../../../../../../redux/slices/project-validation";
import { getDevelopmentCurrentStatus } from "../integration-steps/development";

const ValidationPage: React.FC = () => {
  const { integrationId, projectName, partnerID } = useParams<{
    integrationId: string;
    projectName: string;
    partnerID: string;
  }>();
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { integrationDetail, projectDetails } = useAppSelector((state) => state.projects);
  const isHIDUser = useIsHidStaff();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState<string>("validation");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Project details are already loaded by parent ProjectDetails (same route layout); we only read from store for isDevelopmentCompleted.

  // Fetch integration detail when integrationId is available
  useEffect(() => {
    if (integrationId) {
      dispatch(getIntegrationDetailSagaAction(Number(integrationId)));
    }
  }, [integrationId, dispatch]);

  // Derive whether Development step allows validation actions.
  // Partner/data-entry Save/Submit should be enabled when Development is either
  // "Completed" or "Not applicable".
  const isDevelopmentCompleted = useMemo(() => {
    if (!projectDetails?.technologies || !integrationId) return false; // disable until we have data and know status
    const integration = projectDetails.technologies
      .flatMap((tech) => tech.integrations || [])
      .find((int) => int.integration_id === Number(integrationId));
    const status = getDevelopmentCurrentStatus(integration);
    return status === "Completed" || status === "Not applicable";
  }, [projectDetails, integrationId]);

  // Cleanup: Reset validation documentation state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(validationDocumentationSliceAction.resetValidationState());
      dispatch(projectValidationSliceAction.resetEnvironmentalDetails());
    };
  }, [dispatch]);
  // Check if we're on a history route
  const isHistoryRoute = location.pathname.includes("/history");

  const handleTabChange = (_: any, key: string) => {
    setActiveTab(key);
  };

  const tabItems = [
    {
      key: "validation",
      label: "Validation",
      content: isHistoryRoute ? (
        <Outlet />
      ) : (
        <ValidationTab
          isHIDUser={isHIDUser}
          integrationId={integrationId}
          isDevelopmentCompleted={isDevelopmentCompleted}
        />
      ),
    },
    {
      key: "whitelabel",
      label: "Whitelabel",
      content: <WhitelabelTab isHIDUser={isHIDUser} />,
    },
  ];

  return (
    <div className="validation-page-container">
      <div className="validation-page-wrapper">
        <div className="validation-header-section">
          <Breadcrumbs noTrailingSlash={true}>
            <BreadcrumbItem
              name="Projects"
              onClick={() => {
                navigate(`/partner/${partnerID}/dashboard/projects`);
              }}
              breadcrumbSeparator={<Iconography icon="chevronRight" />}
              isCurrentPage={false}
            />
            <BreadcrumbItem
              name={projectName}
              onClick={() => {
                navigate(
                  `/partner/${partnerID}/dashboard/projects/${projectName}`
                );
              }}
              breadcrumbSeparator={<Iconography icon="chevronRight" />}
              isCurrentPage={false}
            />
            {integrationDetail?.technology ? (
              <BreadcrumbItem
                name={integrationDetail.technology}
                onClick={() => {
                  navigate(
                    `/partner/${partnerID}/dashboard/projects/${projectName}`,
                    { state: { selectedTechnology: integrationDetail.technology } }
                  );
                }}
                breadcrumbSeparator={<Iconography icon="chevronRight" />}
                isCurrentPage={false}
              />
            ) : null}
            <BreadcrumbItem
              name={integrationDetail?.integration || ""}
              onClick={() => {}}
              breadcrumbSeparator={<Iconography icon="chevronRight" />}
              isCurrentPage={true}
            />
          </Breadcrumbs>
          <div className="header-title-row">
            <h1>Validation</h1>
          </div>
          {/* <p className="header-description">
            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod
            tempor incididunt ut labore et dolore magna aliqua.
          </p> */}
        </div>

        <div className="validation-tabs-container">
          <Tabs
            theme="frameless"
            selectedKey={activeTab}
            items={tabItems}
            onClick={handleTabChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ValidationPage;
